import { useState } from 'react';
import { Instagram } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TID } from '../lib/testIds';

export default function Footer() {
  const [legal, setLegal] = useState(null); // 'privacy' | 'terms' | null

  return (
    <footer
      data-testid={TID.footerRoot}
      className="relative bg-transparent border-t border-white/10 overflow-hidden"
    >
      {/* Giant outline mark */}
      <div className="px-6 md:px-14 pt-20 pb-10 overflow-hidden">
        <p
          aria-hidden
          className="font-display font-black leading-none text-transparent select-none text-center w-full"
          style={{
            fontSize: 'clamp(56px, 16vw, 320px)',
            letterSpacing: '0.05em',
            WebkitTextStroke: '1px rgba(255,255,255,0.18)',
          }}
        >
          UTMEDIA
        </p>
      </div>

      <div className="px-6 md:px-14 pb-10 grid grid-cols-2 md:grid-cols-4 gap-10 border-t border-white/10 pt-12">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Contact</p>
          <a
            data-testid={TID.footerEmail}
            href="mailto:hello@utmedia.com.au"
            className="block text-white/80 hover:text-white transition-colors"
          >
            hello@utmedia.com.au
          </a>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Studio</p>
          <p className="text-white/80">Newcastle</p>
          <p className="text-white/80">Sydney</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Services</p>
          <p className="text-white/80">Brand · Wedding</p>
          <p className="text-white/80">Sport · Music</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Follow</p>
          <a
            href="https://instagram.com/utmedia_"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="UTMEDIA on Instagram"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <Instagram size={18} />
            <span className="text-sm">@utmedia_</span>
          </a>
        </div>
      </div>

      <div className="px-6 md:px-14 py-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-white/40">
        <p>© {new Date().getFullYear()} UTMEDIA · All rights reserved</p>
        <div className="flex items-center gap-5">
          <button
            type="button"
            data-testid={TID.footerPrivacy}
            onClick={() => setLegal('privacy')}
            className="uppercase tracking-[0.3em] text-white/40 hover:text-[#D8FF3A] transition-colors"
          >
            Privacy Policy
          </button>
          <span className="text-white/20">·</span>
          <button
            type="button"
            data-testid={TID.footerTerms}
            onClick={() => setLegal('terms')}
            className="uppercase tracking-[0.3em] text-white/40 hover:text-[#D8FF3A] transition-colors"
          >
            Terms of Service
          </button>
        </div>
        <p>Crafted on Awabakal land · Newcastle AU</p>
      </div>

      <LegalDialog
        open={legal === 'privacy'}
        onClose={() => setLegal(null)}
        testid={TID.privacyModal}
        title="Privacy Policy"
        sections={PRIVACY}
      />
      <LegalDialog
        open={legal === 'terms'}
        onClose={() => setLegal(null)}
        testid={TID.termsModal}
        title="Terms of Service"
        sections={TERMS}
      />
    </footer>
  );
}

function LegalDialog({ open, onClose, testid, title, sections }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={testid}
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 text-white"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight text-white">
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
          Last updated · June 2026
        </p>
        <div className="space-y-5">
          {sections.map((s) => (
            <div key={s.h}>
              <h4 className="text-sm font-semibold text-white mb-1.5">{s.h}</h4>
              <p className="text-sm text-white/65 leading-relaxed">{s.p}</p>
            </div>
          ))}
          <p className="text-sm text-white/65 leading-relaxed pt-2 border-t border-white/10">
            Questions? Email{' '}
            <a href="mailto:hello@utmedia.com.au" className="text-[#D8FF3A] hover:underline">
              hello@utmedia.com.au
            </a>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const PRIVACY = [
  {
    h: '1. Who we are',
    p: 'UTMEDIA is a visual content studio based in Newcastle and Sydney, Australia. This policy explains how we collect, use and protect the information you share with us.',
  },
  {
    h: '2. Information we collect',
    p: 'When you submit an enquiry we collect your name, email address, project type, optional budget and the details of your message. We do not collect payment information through this website.',
  },
  {
    h: '3. How we use it',
    p: 'We use your information solely to respond to your enquiry, prepare quotes and proposals, and communicate with you about your project. We never sell your data.',
  },
  {
    h: '4. Storage & security',
    p: 'Enquiries are stored securely and accessed only by the UTMEDIA team. We retain enquiry data for as long as needed to service your request or as required by Australian law.',
  },
  {
    h: '5. Your rights',
    p: 'You can request access to, correction of, or deletion of your personal information at any time by emailing hello@utmedia.com.au.',
  },
  {
    h: '6. Third parties',
    p: 'We may use trusted service providers (e.g. hosting and email) to operate our business. These providers are bound to keep your information confidential.',
  },
];

const TERMS = [
  {
    h: '1. Overview',
    p: 'These terms govern your use of the UTMEDIA website and any enquiry you submit. By using this site you agree to these terms.',
  },
  {
    h: '2. Enquiries & quotes',
    p: 'Submitting an enquiry does not create a binding contract. Project scope, pricing and timelines are confirmed in a separate written agreement before any work begins.',
  },
  {
    h: '3. Pricing',
    p: 'Prices shown on this site are indicative starting points in AUD and may vary based on scope, location, crew and deliverables. Subscription packages are billed monthly and may be cancelled in line with your agreement.',
  },
  {
    h: '4. Intellectual property',
    p: 'All website content, branding and showreel footage remain the property of UTMEDIA. Final deliverables and licensing for client projects are defined in your project agreement.',
  },
  {
    h: '5. Liability',
    p: 'The website and its content are provided "as is". To the extent permitted by law, UTMEDIA is not liable for any loss arising from use of this website.',
  },
  {
    h: '6. Governing law',
    p: 'These terms are governed by the laws of New South Wales, Australia.',
  },
];
